import { Employment } from "@oysterhr/schema"
import { SalaryPeriodEnumType } from "../../types"
import { WEEKS_PER_YEAR } from "~/app/utils/constants"

export type GetEstimateSalaryArgs = Partial<
  Pick<
    Employment,
    "salaryAmount" | "fullTimeHoursPerWeek" | "partTimeDaysPerWeek" | "partTimeHoursPerDay"
  >
> & {
  /**
   * Number of monthly payments on a year (12, 13 or 14)
   */
  numberOfPayments?: number
  salaryPeriod?: SalaryPeriodEnumType
}

const DEFAULT_PAYMENTS_PER_YEAR = 12

const getEstimateWeeklySalary = ({
  salaryAmount,
  fullTimeHoursPerWeek,
  partTimeDaysPerWeek,
  partTimeHoursPerDay,
  salaryPeriod,
}: GetEstimateSalaryArgs) => {
  if (!salaryAmount || !salaryPeriod) return null

  if (salaryPeriod === SalaryPeriodEnumType.Yearly) {
    return salaryAmount / WEEKS_PER_YEAR
  }

  // salaryPeriod is Hourly - create a separate constant for better readability
  const hourlySalaryRate = salaryAmount

  // Non-US part time
  if (partTimeDaysPerWeek && partTimeHoursPerDay) {
    const partTimeHoursPerWeek = partTimeDaysPerWeek * partTimeHoursPerDay

    return hourlySalaryRate * partTimeHoursPerWeek
  }

  // US part time + non-US full time
  if (fullTimeHoursPerWeek) {
    return hourlySalaryRate * fullTimeHoursPerWeek
  }
  return null
}

/**
 * Calculates the estimated yearly salary.
 */
export const getEstimateYearlySalary = (args: GetEstimateSalaryArgs) => {
  const estimatedWeeklySalary = getEstimateWeeklySalary(args)
  if (!estimatedWeeklySalary) return null

  return estimatedWeeklySalary * WEEKS_PER_YEAR
}

/**
 * Calculates the estimated monthly salary.
 */
export const getEstimateMonthlySalary = (args: GetEstimateSalaryArgs) => {
  const estimatedWeeklySalary = getEstimateWeeklySalary(args)
  if (!estimatedWeeklySalary) return null

  const yearlyEstimate = estimatedWeeklySalary * WEEKS_PER_YEAR
  const numberOfPayments = args.numberOfPayments || DEFAULT_PAYMENTS_PER_YEAR
  return yearlyEstimate / numberOfPayments
}
